import React from 'react';
import styled from 'styled-components';
import { colors, typography } from '../../../styles/theme';

const Overlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
`;

export { Overlay };

const DialogContainer = styled.div`
  background: white;
  border-radius: 16px;
  padding: 24px;
  width: 90%;
  max-width: ${props => props.maxWidth || '500px'};
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.15);
`;

const DialogHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 24px;
`;

const DialogTitle = styled.h3`
  font-family: ${typography.fontFamily};
  font-size: ${typography.fontSize.lg};
  color: ${colors.text.primary};
  margin: 0;
  font-weight: ${typography.fontWeight.medium};
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  padding: 8px;
  color: ${colors.text.secondary};
  font-size: 20px;
  line-height: 1;
  
  &:hover {
    color: ${colors.text.primary};
  }
`;

const DialogContent = styled.div`
  margin-bottom: 24px;
`;

const ButtonGroup = styled.div`
  display: flex;
  justify-content: ${props => props.align || 'flex-end'};
  gap: 12px;
`;

const Button = styled.button`
  padding: 10px 20px;
  border-radius: 8px;
  font-family: ${typography.fontFamily};
  font-size: ${typography.fontSize.base};
  font-weight: ${typography.fontWeight.medium};
  cursor: pointer;
  transition: all 0.2s ease;
  
  &:hover {
    transform: translateY(-1px);
  }
`;

export const CancelButton = styled(Button)`
  background: none;
  border: 1px solid ${colors.border};
  color: ${colors.text.secondary};
  
  &:hover {
    background: ${colors.background};
  }
`;

export const SaveButton = styled(Button)`
  background: ${colors.primary};
  border: none;
  color: white;
  
  &:hover {
    background: ${colors.primary}ee;
  }
`;

const Dialog = ({
  isOpen,
  onClose,
  title,
  children,
  footer,
  maxWidth,
  showCloseButton = true,
}) => {
  if (!isOpen) return null;

  return (
    <Overlay>
      <DialogContainer maxWidth={maxWidth}>
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          {showCloseButton && (
            <CloseButton onClick={onClose}>&times;</CloseButton>
          )}
        </DialogHeader>
        <DialogContent>
          {children}
        </DialogContent>
        {footer && (
          <ButtonGroup>
            {footer}
          </ButtonGroup>
        )}
      </DialogContainer>
    </Overlay>
  );
};

// Success Dialog variant
export const SuccessDialog = styled(DialogContainer)`
  background: ${colors.primary};
  color: white;
  max-width: 500px;
  display: flex;
  flex-direction: column;
  justify-content: center;
  min-height: 300px;
  padding: 40px 24px;
`;

export const SuccessTitle = styled(DialogTitle)`
  color: white;
  text-align: center;
  font-size: ${typography.fontSize.lg};
  line-height: 1.6;
  margin: 0;
  padding: 0 16px;
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
`;

export const SuccessButtonGroup = styled(ButtonGroup)`
  justify-content: space-between;
  margin-top: 40px;
`;

export const OkButton = styled(Button)`
  background: white;
  color: ${colors.primary};
  border: none;
  padding: 12px 48px;
  font-weight: ${typography.fontWeight.bold};
  flex: 1;
  margin-right: 6px;
  margin-left: 6px;
  
  &:hover {
    background: rgba(255, 255, 255, 0.9);
    transform: translateY(-1px);
  }
`;

export const UndoButton = styled(Button)`
  background: transparent;
  color: white;
  border: 2px solid white;
  padding: 12px 48px;
  font-weight: ${typography.fontWeight.medium};
  flex: 1;
  margin-right: 6px;
  margin-left: 6px;
  
  &:hover {
    background: rgba(255, 255, 255, 0.1);
    transform: translateY(-1px);
  }
`;

export default Dialog; 